"use client";

import React from "react";
import Link from "next/link";
import { getLocalizedText } from "@/themes/default/templates/section/utils";
import type { SectionProps } from "@/themes/default/templates/section/types";

/** Split a headline so the final word can carry the prism accent. */
function splitLast(text: string): { lead: string; accent: string } {
  const t = text.trim();
  if (!t) return { lead: "", accent: "" };
  const parts = t.split(/\s+/);
  if (parts.length === 1) return { lead: "", accent: t };
  return { lead: parts.slice(0, -1).join(" "), accent: parts[parts.length - 1] };
}

/**
 * Crystal Call-to-Action — a centered frosted-glass panel with a prism
 * halo (the mockup's "Let's build your stack" contact block).
 */
export function CtaSection({
  section,
  currentLanguage = "en",
}: SectionProps<any>) {
  const s = section as any;
  const eyebrow = s.title ? getLocalizedText(s.title, currentLanguage) : "";
  const headline = s.headline ? getLocalizedText(s.headline, currentLanguage) : "";
  const description = s.description
    ? getLocalizedText(s.description, currentLanguage)
    : "";
  const buttons: any[] = Array.isArray(s.buttons) ? s.buttons : [];
  const { lead, accent } = splitLast(headline);

  return (
    <section
      className="px-4 py-16 sm:px-6 md:py-24 lg:px-8"
      data-section-id={section._id}
      data-section-type={section.type}
    >
      <div className="mx-auto max-w-5xl">
        <div
          className="crystal-glass relative overflow-hidden rounded-3xl px-8 py-16 text-center md:px-12 md:py-20"
        >
          {/* Prism halo. */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-2/3"
            style={{
              background:
                "radial-gradient(60% 70% at 50% 0%, rgba(124,147,245,0.18), transparent 70%)",
            }}
            aria-hidden="true"
          />
          <div className="relative">
            {eyebrow && <span className="crystal-eyebrow">{eyebrow}</span>}
            {headline && (
              <h2
                className="crystal-display mx-auto mt-3 text-3xl md:text-5xl"
                style={{ color: "var(--color-foreground)", textWrap: "balance" }}
              >
                {lead ? (
                  <>
                    <span className="font-extralight">{lead} </span>
                    <span className="crystal-prism-text font-medium">
                      {accent}
                    </span>
                  </>
                ) : (
                  <span className="crystal-prism-text font-medium">{accent}</span>
                )}
              </h2>
            )}
            {description && (
              <p
                className="mx-auto mt-5 max-w-xl text-base leading-relaxed md:text-lg"
                style={{ color: "var(--color-muted-foreground)" }}
              >
                {description}
              </p>
            )}
            {buttons.length > 0 && (
              <div className="mt-9 flex flex-wrap justify-center gap-4">
                {buttons.map((button, i) => {
                  const isPrimary = (button.style ?? "primary") === "primary";
                  return (
                    <Link
                      key={i}
                      href={button.url || "#"}
                      target={button.openInNewTab ? "_blank" : undefined}
                      rel={button.openInNewTab ? "noopener noreferrer" : undefined}
                      className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5 md:text-base"
                      style={
                        isPrimary
                          ? {
                              backgroundColor: "var(--color-primary)",
                              color: "var(--color-primary-foreground)",
                            }
                          : {
                              background: "rgba(255,255,255,0.06)",
                              color: "var(--color-foreground)",
                              border: "1px solid var(--color-border)",
                            }
                      }
                    >
                      {getLocalizedText(button.text, currentLanguage)}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default CtaSection;
